const GAP_PX = 16;
const MOBILE_BREAKPOINT = 750;

const VISIBLE_COLLAPSED_MAX = 4;
const VISIBLE_DESKTOP = 3;
const VISIBLE_MOBILE = 1;

class SWWPillars {
  constructor(root) {
    this.root = root;
    this.wrap = root.querySelector('[data-sww-pillars-wrap]');
    this.track = root.querySelector('[data-sww-pillars-track]');
    if (!this.wrap || !this.track) return;

    this.cards = Array.from(root.querySelectorAll('[data-sww-pillar]'));
    if (this.cards.length === 0) return;

    this.prevBtn = root.querySelector('[data-sww-pillars-prev]');
    this.nextBtn = root.querySelector('[data-sww-pillars-next]');
    this.progressThumb = root.querySelector('[data-sww-pillars-progress]');

    this.offset = 0;
    this.expandedIndex = null;
    this.collapsedCardWidth = 0;
    this.expandedCardWidth = 0;
    this.resizeObserver = null;

    this.bindEvents();

    // Measure after fonts/images settle
    requestAnimationFrame(() => {
      this.measureAndApply();
      this.updateState();
    });
  }

  bindEvents() {
    this.cards.forEach((card, index) => {
      const openBtn = card.querySelector('[data-sww-pillar-open]');
      const closeBtn = card.querySelector('[data-sww-pillar-close]');

      card.addEventListener('click', (e) => {
        // On mobile: no expand interaction — content is always shown.
        if (window.innerWidth < MOBILE_BREAKPOINT) return;
        if (e.target.closest('[data-sww-pillar-close]')) return;
        if (card.getAttribute('aria-expanded') === 'true') return;
        this.expand(index);
      });

      card.addEventListener('keydown', (e) => {
        // On mobile: skip keyboard expand/collapse.
        if (window.innerWidth < MOBILE_BREAKPOINT) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (card.getAttribute('aria-expanded') === 'true') {
            this.collapse();
          } else {
            this.expand(index);
          }
        }
        if (e.key === 'Escape' && card.getAttribute('aria-expanded') === 'true') {
          this.collapse();
        }
      });

      if (openBtn) {
        openBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.expand(index);
        });
      }

      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.collapse();
        });
      }
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.slide(-1));
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.slide(1));
    }

    // Touch swipe support for mobile carousel
    this.bindTouchEvents();

    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.measureAndApply();
      const nowMobile = this.isMobile;

      // Crossed the breakpoint — reset everything
      if (wasMobile !== nowMobile) {
        this.expandedIndex = null;
        this.cards.forEach((card) => {
          card.setAttribute('aria-expanded', 'false');
        });
      }

      this.offset = 0;
      this.track.style.setProperty('--sww-pillars-offset', '0px');
      this.updateState();
    }, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.measureAndApply();
        this.updateState();
      });
      this.resizeObserver.observe(this.wrap);
    }
  }

  bindTouchEvents() {
    let startX = 0;
    let startY = 0;
    let isDragging = false;
    const SWIPE_THRESHOLD = 40;

    this.wrap.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
    }, { passive: true });

    this.wrap.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      const diffX = e.touches[0].clientX - startX;
      const diffY = e.touches[0].clientY - startY;
      // If horizontal movement dominates, it's a swipe
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
        // Prevent vertical scroll during horizontal swipe
        // but only if we're actually swiping (not just tapping)
      }
    }, { passive: true });

    this.wrap.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;
      const endX = e.changedTouches[0].clientX;
      const diffX = endX - startX;
      if (Math.abs(diffX) > SWIPE_THRESHOLD) {
        this.slide(diffX < 0 ? 1 : -1);
      }
    }, { passive: true });
  }

  getVisibleCount() {
    return window.innerWidth < MOBILE_BREAKPOINT
      ? VISIBLE_MOBILE
      : VISIBLE_DESKTOP;
  }

  measureAndApply() {
    const containerWidth = this.wrap.clientWidth;
    const count = this.cards.length;
    if (count === 0 || containerWidth <= 0) return;

    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    let collapsedW;
    let expandedW;

    if (isMobile) {
      // Mobile: one card per slide.
      collapsedW = containerWidth;
      expandedW = containerWidth;
    } else {
      // Desktop: fit up to 4 cards in view at the collapsed state.
      const visibleCollapsed = Math.min(count, VISIBLE_COLLAPSED_MAX);
      const collapsedGaps = GAP_PX * Math.max(0, visibleCollapsed - 1);
      collapsedW = (containerWidth - collapsedGaps) / visibleCollapsed;

      // Expanded state: 1 expanded + 2 collapsed visible in the viewport.
      const visibleSiblings = Math.min(
        VISIBLE_DESKTOP - 1,
        Math.max(0, count - 1)
      );
      const visibleGaps = GAP_PX * Math.max(0, visibleSiblings);

      expandedW =
        containerWidth - visibleSiblings * collapsedW - visibleGaps;

      // Safety clamp
      const minExpanded = collapsedW * 1.6;
      if (expandedW < minExpanded) expandedW = minExpanded;
    }

    this.collapsedCardWidth = collapsedW;
    this.expandedCardWidth = expandedW;
    this.isMobile = isMobile;

    this.track.style.setProperty('--sww-pillars-gap', `${GAP_PX}px`);
    this.track.style.setProperty('--sww-pillars-card-w', `${collapsedW}px`);
    this.track.style.setProperty('--sww-pillars-card-expanded-w', `${expandedW}px`);
  }

  expand(index) {
    if (index < 0 || index >= this.cards.length) return;

    this.expandedIndex = index;

    this.cards.forEach((card, i) => {
      card.setAttribute('aria-expanded', i === index ? 'true' : 'false');
      const closeBtn = card.querySelector('[data-sww-pillar-close]');
      const openBtn = card.querySelector('[data-sww-pillar-open]');
      if (closeBtn) closeBtn.tabIndex = i === index ? 0 : -1;
      if (openBtn) openBtn.tabIndex = i === index ? -1 : 0;
    });

    this.scrollToCard(index);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.updateState());
    });
  }

  collapse() {
    this.expandedIndex = null;
    this.cards.forEach((card) => {
      card.setAttribute('aria-expanded', 'false');
      const closeBtn = card.querySelector('[data-sww-pillar-close]');
      const openBtn = card.querySelector('[data-sww-pillar-open]');
      if (closeBtn) closeBtn.tabIndex = -1;
      if (openBtn) openBtn.tabIndex = 0;
    });

    this.offset = 0;
    this.track.style.setProperty('--sww-pillars-offset', '0px');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.updateState());
    });
  }

  slide(direction) {
    const maxOffset = this.getMaxOffset();
    if (maxOffset === 0) return;

    const step = this.collapsedCardWidth + GAP_PX;
    let newOffset = this.offset - direction * step;

    if (newOffset > 0) newOffset = 0;
    if (newOffset < -maxOffset) newOffset = -maxOffset;

    this.offset = newOffset;
    this.track.style.setProperty('--sww-pillars-offset', `${newOffset}px`);
    this.updateNav();
  }

  scrollToCard(index) {
    requestAnimationFrame(() => {
      const cardLeftInTrack = index * (this.collapsedCardWidth + GAP_PX);
      const cardRightInTrack = cardLeftInTrack + this.expandedCardWidth;

      const containerWidth = this.wrap.clientWidth;
      const currentOffset = this.offset;

      const cardLeftInView = cardLeftInTrack + currentOffset;
      const cardRightInView = cardRightInTrack + currentOffset;

      let targetOffset = currentOffset;

      if (cardRightInView > containerWidth) {
        targetOffset = -(cardRightInTrack - containerWidth);
      }

      if (cardLeftInView < 0) {
        targetOffset = -cardLeftInTrack;
      }

      // Clamp
      const maxOffset = this.getMaxOffset();
      if (targetOffset > 0) targetOffset = 0;
      if (targetOffset < -maxOffset) targetOffset = -maxOffset;

      this.offset = targetOffset;
      this.track.style.setProperty('--sww-pillars-offset', `${targetOffset}px`);
      this.updateNav();
    });
  }

  getMaxOffset() {
    const containerWidth = this.wrap.clientWidth;
    const count = this.cards.length;
    if (count === 0) return 0;

    const hasExpanded = this.expandedIndex !== null;
    const collapsedCount = hasExpanded ? count - 1 : count;
    const expandedCount = hasExpanded ? 1 : 0;

    const totalCardWidth =
      collapsedCount * this.collapsedCardWidth +
      expandedCount * this.expandedCardWidth;
    const totalGaps = GAP_PX * Math.max(0, count - 1);
    const trackWidth = totalCardWidth + totalGaps;

    return Math.max(0, trackWidth - containerWidth);
  }

  updateState() {
    this.updateNav();
    this.updateProgress();
  }

  updateNav() {
    if (!this.prevBtn || !this.nextBtn) return;

    const maxOffset = this.getMaxOffset();

    if (maxOffset < 1) {
      this.prevBtn.disabled = true;
      this.nextBtn.disabled = true;
      return;
    }

    this.prevBtn.disabled = this.offset >= 0;
    this.nextBtn.disabled = this.offset <= -maxOffset + 1;

    this.updateProgress();
  }

  updateProgress() {
    if (!this.progressThumb) return;

    const containerWidth = this.wrap.clientWidth;
    const count = this.cards.length;
    if (count === 0) return;

    const hasExpanded = this.expandedIndex !== null;
    const collapsedCount = hasExpanded ? count - 1 : count;
    const expandedCount = hasExpanded ? 1 : 0;
    const totalCardWidth =
      collapsedCount * this.collapsedCardWidth +
      expandedCount * this.expandedCardWidth;
    const totalGaps = GAP_PX * Math.max(0, count - 1);
    const trackWidth = totalCardWidth + totalGaps;

    if (trackWidth <= containerWidth) {
      this.progressThumb.style.setProperty('--sww-pillars-thumb-scale', '1');
      this.progressThumb.style.setProperty('--sww-pillars-thumb-offset', '0%');
      return;
    }

    const visibleRatio = containerWidth / trackWidth;
    const maxOffset = trackWidth - containerWidth;
    const scrolledRatio = maxOffset > 0 ? Math.abs(this.offset) / maxOffset : 0;

    const movableSpacePct = (1 - visibleRatio) * 100;
    const translatePct =
      visibleRatio > 0 ? (scrolledRatio * movableSpacePct) / visibleRatio : 0;

    this.progressThumb.style.setProperty(
      '--sww-pillars-thumb-scale',
      visibleRatio.toFixed(4)
    );
    this.progressThumb.style.setProperty(
      '--sww-pillars-thumb-offset',
      `${translatePct.toFixed(2)}%`
    );
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

const initSWWPillars = (scope = document) => {
  scope.querySelectorAll('[data-sww-pillars]').forEach((el) => {
    if (el.__swwPillars) return;
    el.__swwPillars = new SWWPillars(el);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initSWWPillars());
} else {
  initSWWPillars();
}

document.addEventListener('shopify:section:load', (event) => {
  initSWWPillars(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector('[data-sww-pillars]');
  if (root && root.__swwPillars) {
    root.__swwPillars.destroy();
    delete root.__swwPillars;
  }
});

document.addEventListener('shopify:block:select', (event) => {
  const card = event.target.closest('[data-sww-pillar]');
  if (!card) return;
  const root = card.closest('[data-sww-pillars]');
  if (root && root.__swwPillars) {
    const index = parseInt(card.dataset.pillarIndex, 10);
    root.__swwPillars.expand(index);
  }
});

document.addEventListener('shopify:block:deselect', (event) => {
  const card = event.target.closest('[data-sww-pillar]');
  if (!card) return;
  const root = card.closest('[data-sww-pillars]');
  if (root && root.__swwPillars) {
    root.__swwPillars.collapse();
  }
});
